/**
 * ZONE MOCKS — handler MSW delle zone commerciali e delle loro province, con
 * gli stessi codici del backend: 400 nome/colore/provincia/utente non validi,
 * 404 zona o provincia inesistente, 409 nome già usato o provincia già in una
 * zona del sito. Lo stato vive in memoria.
 */

import { http, HttpResponse } from 'msw';
import type { Province, Zone, ZoneBusiness, ZoneRegion, ZoneUser } from '../../entities/zone/zoneTypes';
import { mockBusinesses } from './businessMocks';
import { MOCK_GROUP_ID } from './mockSite';

const API = '/o/crm/v1_0';

// Ordinate per nome, come le restituisce il backend.
const mockProvinces: Province[] = [
  { regionId: 1004, regionCode: 'MI', name: 'Milano' },
  { regionId: 1003, regionCode: 'NA', name: 'Napoli' },
  { regionId: 1002, regionCode: 'RM', name: 'Roma' },
  { regionId: 1001, regionCode: 'TO', name: 'Torino' },
];

export const mockUsers: ZoneUser[] = [
  { userId: 101, fullName: 'Anna Bianchi' },
  { userId: 102, fullName: 'Marco Rossi' },
  { userId: 1, fullName: 'Jader Francia' },
];

const audit = {
  companyId: 1,
  groupId: MOCK_GROUP_ID,
  userId: 1,
  userName: 'Sistema',
  createDate: '2026-03-01T10:00:00Z',
  modifiedDate: '2026-03-01T10:00:00Z',
};

const region = (zoneRegionId: number, zoneId: number, regionId: number, color = '', liferayUserId = 0): ZoneRegion => {
  const p = mockProvinces.find((x) => x.regionId === regionId)!;
  return {
    zoneRegionId, zoneId, regionId, regionCode: p.regionCode, regionName: p.name, color, liferayUserId,
    liferayUserName: mockUsers.find((u) => u.userId === liferayUserId)?.fullName ?? null,
  };
};

const mockZones: Zone[] = [
  // La zona delle due aziende milanesi ha per commerciale l'utente del login finto (1): è lui che le
  // vede come «sue» (zoneAgentOf), come nel backend, dove il commerciale di un'azienda è quello della zona.
  { ...audit, zoneId: 1, name: 'Nord', color: '#1976d2', liferayUserId: 1, liferayUserName: 'Jader Francia', customerCount: 2,
    regions: [region(1, 1, 1004), region(2, 1, 1001, '#43a047', 102)] },
  { ...audit, zoneId: 2, name: 'Centro', color: '#e53935', liferayUserId: 0, liferayUserName: null, customerCount: 0,
    regions: [region(3, 2, 1002)] },
];

// Le aziende di ogni zona, per id: i nomi si leggono da businessMocks, così la zona mostra le
// stesse aziende della scheda. La zona 1 (Nord, con la provincia di Milano) ha le due aziende
// milanesi dei mock, e ne ha due apposta: prova la conferma di eliminazione.
const zoneBusinessIds: Record<number, number[]> = {
  1: [3, 11],
};

const zoneBusinesses = (zoneId: number): ZoneBusiness[] =>
  (zoneBusinessIds[zoneId] ?? []).map((businessId) => ({
    businessId,
    name: mockBusinesses.find((b) => b.businessId === businessId)?.name ?? '',
  }));

/** Il commerciale della zona dell'azienda, 0 se l'azienda non sta in una zona o la zona non ha commerciale. */
export const zoneAgentOf = (businessId: number): number => {
  const zoneId = Object.keys(zoneBusinessIds).map(Number).find((id) => zoneBusinessIds[id].includes(businessId));
  return mockZones.find((z) => z.zoneId === zoneId)?.liferayUserId ?? 0;
};

let nextZoneId = 3;
let nextZoneRegionId = 4;

const page = <T>(items: T[]) => ({
  items,
  page: 1,
  pageSize: items.length,
  totalCount: items.length,
  lastPage: 1,
});

const bad = (type: string, title: string, status = 400) =>
  HttpResponse.json({ type, title }, { status });

const COLOR = /^#[0-9a-fA-F]{6}$/;

interface Body {
  name?: string;
  regionId?: number;
  color?: string;
  liferayUserId?: number;
}

/** Validazione comune a zone e province, sui soli campi presenti. `color: ''` è ammesso solo per le province. */
const validate = (b: Body, emptyColorOk = false) => {
  if (b.name !== undefined && !b.name.trim()) return bad('InvalidName', 'Il nome della zona è obbligatorio');
  if (b.color !== undefined && !(emptyColorOk && b.color === '') && !COLOR.test(b.color)) {
    return bad('InvalidColor', 'Il colore deve essere nel formato #rrggbb');
  }
  if (b.regionId !== undefined && !mockProvinces.some((p) => p.regionId === b.regionId)) {
    return bad('InvalidRegion', 'Provincia non valida');
  }
  if (b.liferayUserId && !mockUsers.some((u) => u.userId === b.liferayUserId)) {
    return bad('InvalidUser', 'Utente non valido');
  }
  return null;
};

const nameTaken = (name: string, zoneId: number) =>
  mockZones.some((z) => z.zoneId !== zoneId && z.name.trim().toLowerCase() === name.trim().toLowerCase());

const regionTaken = (regionId: number, zoneRegionId: number) =>
  mockZones.some((z) => z.regions.some((r) => r.zoneRegionId !== zoneRegionId && r.regionId === regionId));

const findRegion = (zoneRegionId: number) => {
  for (const z of mockZones) {
    const idx = z.regions.findIndex((r) => r.zoneRegionId === zoneRegionId);
    if (idx >= 0) return { zone: z, idx };
  }
  return null;
};

const applyZone = (zone: Zone, b: Body): Zone => {
  const userId = b.liferayUserId ?? zone.liferayUserId;
  return {
    ...zone,
    name: b.name?.trim() ?? zone.name,
    color: (b.color ?? zone.color).toLowerCase(),
    liferayUserId: userId,
    liferayUserName: mockUsers.find((u) => u.userId === userId)?.fullName ?? null,
    modifiedDate: new Date().toISOString(),
  };
};

export const zoneHandlers = [
  http.get(`${API}/zones/:groupId`, () => HttpResponse.json(page([...mockZones].sort((a, b) => a.name.localeCompare(b.name))))),

  http.post(`${API}/zones/:groupId`, async ({ params, request }) => {
    const body = (await request.json()) as Body;
    if (body.name === undefined || body.color === undefined) {
      return bad('InvalidName', 'Nome e colore sono obbligatori');
    }
    const invalid = validate(body);
    if (invalid) return invalid;
    if (nameTaken(body.name, 0)) return bad('DuplicateName', `Esiste già una zona con nome ${body.name.trim()}`, 409);
    const blank: Zone = { ...audit, groupId: Number(params.groupId), zoneId: nextZoneId++, name: '', color: '', liferayUserId: 0, liferayUserName: null, customerCount: 0, regions: [] };
    const created = applyZone(blank, body);
    mockZones.push(created);
    return HttpResponse.json(created, { status: 200 });
  }),

  http.patch(`${API}/zone/:zoneId`, async ({ params, request }) => {
    const idx = mockZones.findIndex((z) => z.zoneId === Number(params.zoneId));
    if (idx < 0) return bad('NotFound', 'Zona non trovata', 404);
    const body = (await request.json()) as Body;
    const invalid = validate(body);
    if (invalid) return invalid;
    if (body.name !== undefined && nameTaken(body.name, mockZones[idx].zoneId)) {
      return bad('DuplicateName', `Esiste già una zona con nome ${body.name.trim()}`, 409);
    }
    mockZones[idx] = applyZone(mockZones[idx], body);
    return HttpResponse.json(mockZones[idx]);
  }),

  // Le aziende collegate non si cancellano: restano senza zona. Le province sì.
  http.delete(`${API}/zone/:zoneId`, ({ params }) => {
    const idx = mockZones.findIndex((z) => z.zoneId === Number(params.zoneId));
    if (idx < 0) return bad('NotFound', 'Zona non trovata', 404);
    delete zoneBusinessIds[mockZones[idx].zoneId];
    mockZones.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  http.get(`${API}/zone/:zoneId/businesses`, ({ params }) => {
    if (!mockZones.some((z) => z.zoneId === Number(params.zoneId))) {
      return bad('NotFound', 'Zona non trovata', 404);
    }
    return HttpResponse.json(page(zoneBusinesses(Number(params.zoneId))));
  }),

  http.post(`${API}/zone/:zoneId/regions`, async ({ params, request }) => {
    const zone = mockZones.find((z) => z.zoneId === Number(params.zoneId));
    if (!zone) return bad('NotFound', 'Zona non trovata', 404);
    const body = (await request.json()) as Body;
    if (body.regionId === undefined) return bad('InvalidRegion', 'Provincia non valida');
    const invalid = validate(body, true);
    if (invalid) return invalid;
    if (regionTaken(body.regionId, 0)) return bad('DuplicateRegion', 'La provincia ha già una zona', 409);
    const created = region(nextZoneRegionId++, zone.zoneId, body.regionId, body.color?.toLowerCase() ?? '', body.liferayUserId ?? 0);
    zone.regions.push(created);
    return HttpResponse.json(created);
  }),

  http.patch(`${API}/zone-region/:zoneRegionId`, async ({ params, request }) => {
    const found = findRegion(Number(params.zoneRegionId));
    if (!found) return bad('NotFound', 'Provincia della zona non trovata', 404);
    const body = (await request.json()) as Body;
    const invalid = validate(body, true);
    if (invalid) return invalid;
    const old = found.zone.regions[found.idx];
    if (body.regionId !== undefined && regionTaken(body.regionId, old.zoneRegionId)) {
      return bad('DuplicateRegion', 'La provincia ha già una zona', 409);
    }
    const updated = region(old.zoneRegionId, old.zoneId, body.regionId ?? old.regionId,
      body.color?.toLowerCase() ?? old.color, body.liferayUserId ?? old.liferayUserId);
    found.zone.regions[found.idx] = updated;
    return HttpResponse.json(updated);
  }),

  http.delete(`${API}/zone-region/:zoneRegionId`, ({ params }) => {
    const found = findRegion(Number(params.zoneRegionId));
    if (!found) return bad('NotFound', 'Provincia della zona non trovata', 404);
    found.zone.regions.splice(found.idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  http.get(`${API}/provinces`, () => HttpResponse.json(page(mockProvinces))),
  http.get(`${API}/zone-users`, () => HttpResponse.json(page(mockUsers))),
];
